/**
 * Form for creating/editing a ticket type using URQL mutations.
 */

import { useState, useEffect } from "react";
import {
  haptic,
  safeShowAlert,
  safeShowConfirm,
} from "../../../../lib/telegram.ts";
import { getErrorMessage } from "../../../../lib/graphql.ts";
import { InlineTextField } from "./InlineFields.tsx";
import {
  useCreateTicketType,
  useUpdateTicketType,
  useDeleteTicketType,
} from "../hooks/useTicketTypeMutations.ts";
import type { TicketType } from "../types.ts";

const CURRENCIES = ["THB", "RUB"] as const;

export interface TicketTypeFormProps {
  ticketType: TicketType | null | "new";
  eventId: string;
  onSave: () => void;
  onDelete?: () => void;
  onCancel: () => void;
  serviceFeeConfigId: string | null;
  serviceFeePrices: Record<string, string>;
  setServiceFeePrices: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
}

export function TicketTypeForm({
  ticketType,
  eventId,
  onSave,
  onDelete,
  onCancel,
  serviceFeeConfigId,
  serviceFeePrices,
  setServiceFeePrices,
}: TicketTypeFormProps) {
  const isNew = ticketType === "new";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prices, setPrices] = useState<Record<string, string>>({
    THB: "",
    RUB: "",
  });
  const [maxQuantity, setMaxQuantity] = useState("");

  // URQL mutation hooks
  const { createTicketType, isLoading: isCreating } = useCreateTicketType();
  const { updateTicketType, isLoading: isUpdating } = useUpdateTicketType();
  const { deleteTicketType, isLoading: isDeleting } = useDeleteTicketType();

  const isSaving = isCreating || isUpdating;

  useEffect(() => {
    if (ticketType && ticketType !== "new") {
      setName(ticketType.name);
      setDescription(ticketType.description ?? "");
      const pricesMap: Record<string, string> = { THB: "", RUB: "" };
      for (const p of ticketType.prices || []) {
        pricesMap[p.currency] = String(p.price);
      }
      setPrices(pricesMap);
      setMaxQuantity(
        ticketType.maxQuantity != null ? String(ticketType.maxQuantity) : ""
      );
    } else if (isNew) {
      setName("");
      setDescription("");
      setPrices({ THB: "", RUB: "" });
      setMaxQuantity("");
    }
  }, [ticketType, isNew]);

  const handleSave = async () => {
    if (!name.trim()) {
      safeShowAlert("Введите название типа билета");
      return;
    }
    const pricesArray: Array<{ currency: string; price: number }> = [];
    for (const currency of CURRENCIES) {
      const val = prices[currency];
      if (val && val.trim() !== "") {
        const priceNum = parseFloat(val);
        if (isNaN(priceNum) || priceNum < 0) {
          safeShowAlert(`Укажите корректную цену для ${currency}`);
          return;
        }
        pricesArray.push({ currency, price: priceNum });
      }
    }
    if (pricesArray.length === 0) {
      safeShowAlert("Укажите хотя бы одну цену");
      return;
    }

    try {
      if (isNew) {
        await createTicketType(eventId, {
          name: name.trim(),
          description: description.trim() || null,
          prices: pricesArray,
          maxQuantity: maxQuantity ? parseInt(maxQuantity, 10) : null,
          sortOrder: 0,
          isActive: true,
        });
      } else if (
        ticketType &&
        typeof ticketType === "object" &&
        "id" in ticketType
      ) {
        const feePricesArray = Object.entries(serviceFeePrices)
          .filter(([, v]) => v.trim() !== "")
          .map(([currency, amount]) => ({
            currency,
            amount: String(Number(amount)),
          }));
        await updateTicketType(ticketType.id, {
          name: name.trim(),
          description: description.trim() || null,
          prices: pricesArray,
          maxQuantity: maxQuantity ? parseInt(maxQuantity, 10) : null,
          isActive: ticketType.isActive,
          ...(serviceFeeConfigId && feePricesArray.length > 0
            ? { serviceFeeConfigId, serviceFeePrices: feePricesArray }
            : {}),
        });
      }
      haptic("success");
      safeShowAlert("Сохранено");
      onSave();
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Не удалось сохранить"));
    }
  };

  const handleDelete = async () => {
    if (!ticketType || ticketType === "new" || !onDelete) return;
    const ok = await new Promise<boolean>((resolve) =>
      safeShowConfirm("Удалить этот тип билета?", (confirmed: boolean) =>
        resolve(confirmed)
      )
    );
    if (!ok) return;

    try {
      await deleteTicketType(ticketType.id);
      haptic("success");
      onDelete();
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Не удалось удалить"));
    }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 mb-3 space-y-3">
      <h3 className="font-semibold text-slate-900 text-[15px]">
        {isNew ? "Новый тип билета" : "Редактирование"}
      </h3>

      <div className="card overflow-hidden">
        <InlineTextField
          label="Название"
          value={name}
          onChange={setName}
          placeholder="Стандартный"
          required
        />
        <InlineTextField
          label="Описание"
          value={description}
          onChange={setDescription}
          placeholder="Краткое описание"
        />
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-[13px] text-slate-500 mb-2">Цены</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] text-slate-400 mb-1">
                THB (฿)
              </label>
              <input
                type="number"
                value={prices.THB}
                onChange={(e) => setPrices({ ...prices, THB: e.target.value })}
                placeholder="250"
                className="w-full px-3 py-2 text-[15px] bg-slate-50 rounded-lg outline-none"
              />
            </div>
            <div>
              <label className="block text-[12px] text-slate-400 mb-1">
                RUB (₽)
              </label>
              <input
                type="number"
                value={prices.RUB}
                onChange={(e) => setPrices({ ...prices, RUB: e.target.value })}
                placeholder="750"
                className="w-full px-3 py-2 text-[15px] bg-slate-50 rounded-lg outline-none"
              />
            </div>
          </div>
        </div>

        {serviceFeeConfigId && (
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-[13px] text-slate-500 mb-2">Сервисный сбор</p>
            {prices.RUB?.trim() !== "" &&
              parseFloat(prices.RUB) > 0 &&
              (!serviceFeePrices.RUB?.trim() ||
                isNaN(parseFloat(serviceFeePrices.RUB)) ||
                parseFloat(serviceFeePrices.RUB) < 0) && (
                <div className="mb-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200/60">
                  <p className="text-[12px] text-amber-800 font-medium">
                    У билета указана цена в рублях, но у сервисного сбора не
                    заполнена сумма для RUB. При оплате в рублях сбор не будет
                    начислен.
                  </p>
                </div>
              )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] text-slate-400 mb-1">
                  THB (฿)
                </label>
                <input
                  type="number"
                  value={serviceFeePrices.THB ?? ""}
                  onChange={(e) =>
                    setServiceFeePrices((p) => ({ ...p, THB: e.target.value }))
                  }
                  placeholder="25"
                  className="w-full px-3 py-2 text-[15px] bg-slate-50 rounded-lg outline-none"
                />
              </div>
              <div>
                <label className="block text-[12px] text-slate-400 mb-1">
                  RUB (₽)
                </label>
                <input
                  type="number"
                  value={serviceFeePrices.RUB ?? ""}
                  onChange={(e) =>
                    setServiceFeePrices((p) => ({ ...p, RUB: e.target.value }))
                  }
                  placeholder="75"
                  className="w-full px-3 py-2 text-[15px] bg-slate-50 rounded-lg outline-none"
                />
              </div>
            </div>
          </div>
        )}

        <InlineTextField
          label="Лимит"
          value={maxQuantity}
          onChange={setMaxQuantity}
          placeholder="Без ограничения"
          type="number"
        />
      </div>

      <div className="flex items-center gap-2 pt-2">
        {!isNew && onDelete && (
          <button
            onClick={handleDelete}
            disabled={isDeleting || isSaving}
            className="px-3 py-2 text-[14px] font-medium text-red-600 active:bg-red-50 rounded-xl transition-colors disabled:opacity-50"
          >
            {isDeleting ? "..." : "Удалить"}
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={onCancel}
          className="px-4 py-2 text-[14px] font-medium text-slate-600 bg-slate-100 active:bg-slate-200 rounded-xl transition-colors"
        >
          Отмена
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving || isDeleting}
          className="px-4 py-2 text-[14px] font-medium text-white bg-[var(--color-accent)] active:opacity-90 rounded-xl transition-colors disabled:opacity-50"
        >
          {isSaving ? "..." : "Сохранить"}
        </button>
      </div>
    </div>
  );
}
